import * as THREE from 'three';

const VERT = /* glsl */ `
attribute float depth;
varying float vDepth;
varying vec3 vWorld;
#include <fog_pars_vertex>
void main() {
  vDepth = depth;
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vWorld = wp.xyz;
  vec4 mvPosition = viewMatrix * wp;
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}`;

const FRAG = /* glsl */ `
uniform float uTime;
uniform vec3 uShallow;
uniform vec3 uDeep;
uniform vec3 uSky;
uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform float uOpen;
varying float vDepth;
varying vec3 vWorld;
#include <fog_pars_fragment>
float h(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float n(vec2 p){ vec2 i=floor(p); vec2 f=fract(p); vec2 u=f*f*(3.0-2.0*f);
  return mix(mix(h(i),h(i+vec2(1,0)),u.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),u.x),u.y); }
float waves(vec2 p) {
  return n(p * 0.45 + vec2(uTime * 0.12, uTime * 0.05)) * 0.6 + n(p * 1.1 - vec2(uTime * 0.07, uTime * 0.16)) * 0.4;
}
void main() {
  vec2 p = vWorld.xz * mix(1.0, 0.55, uOpen);
  float e = 0.15;
  float hC = waves(p);
  vec3 nrm = normalize(vec3(hC - waves(p + vec2(e, 0.0)), mix(0.35, 0.28, uOpen), hC - waves(p + vec2(0.0, e))));
  vec3 viewDir = normalize(cameraPosition - vWorld);
  float fres = pow(1.0 - max(dot(nrm, viewDir), 0.0), 3.0);
  float d = clamp(vDepth / mix(2.5, 6.0, uOpen), 0.0, 1.0);
  vec3 col = mix(uShallow, uDeep, d);
  col = mix(col, uSky, fres * mix(0.55, 0.65, uOpen) + uOpen * 0.03);
  vec3 halfV = normalize(uSunDir + viewDir);
  float spec = pow(max(dot(nrm, halfV), 0.0), 120.0);
  col += uSunColor * spec * 2.5;
  // Open sea: sun glitter (HDR sparkles that bloom) and slow swell bands.
  if (uOpen > 0.5) {
    float glint = n(vWorld.xz * 3.1 + vec2(uTime * 0.7, -uTime * 0.4)) * n(vWorld.xz * 5.3 - vec2(uTime * 0.5, uTime * 0.9));
    float sunward = pow(max(dot(nrm, halfV), 0.0), 18.0);
    col += uSunColor * smoothstep(0.7, 0.86, glint) * 2.6 * (0.03 + sunward * 1.6);
    col *= 0.93 + 0.1 * n(vWorld.xz * 0.05 + uTime * 0.03);
  }
  // Shoreline foam.
  float foamLine = smoothstep(0.35, 0.0, vDepth) * (0.6 + 0.4 * n(p * 2.0 + uTime * 0.5));
  col = mix(col, vec3(0.92, 0.95, 0.98), foamLine * 0.8);
  float alpha = mix(0.55, 0.92, d) + foamLine * 0.3;
  gl_FragColor = vec4(col, clamp(alpha, 0.0, 1.0));
  #include <fog_fragment>
}`;

/**
 * Stylised water sheet: depth-tinted (shallow teal to deep navy), animated
 * value-noise normals, fresnel sky reflection, sun glint and shoreline foam.
 * Depth is baked per vertex from the terrain height, so no depth texture is
 * needed.
 */
export class Water {
  readonly mesh: THREE.Mesh;
  private mat: THREE.ShaderMaterial;

  constructor(minX: number, minZ: number, width: number, depth: number, level: number, heightAt: (x: number, z: number) => number, opts: { shallow?: number; deep?: number; sky?: number; open?: boolean } = {}) {
    const res = 2;
    const nx = Math.ceil(width / res);
    const nz = Math.ceil(depth / res);
    const geo = new THREE.PlaneGeometry(width, depth, nx, nz);
    geo.rotateX(-Math.PI / 2);
    geo.translate(minX + width / 2, level, minZ + depth / 2);
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    const d = new Float32Array(pos.count);
    for (let i = 0; i < pos.count; i++) d[i] = level - heightAt(pos.getX(i), pos.getZ(i));
    geo.setAttribute('depth', new THREE.BufferAttribute(d, 1));
    // Drop triangles that are entirely above ground to save fill-rate.
    const idx = geo.getIndex();
    if (idx) {
      const keep: number[] = [];
      for (let i = 0; i < idx.count; i += 3) {
        const a = idx.getX(i);
        const b = idx.getX(i + 1);
        const c = idx.getX(i + 2);
        if ((d[a] as number) > -0.4 || (d[b] as number) > -0.4 || (d[c] as number) > -0.4) keep.push(a, b, c);
      }
      geo.setIndex(keep);
    }
    this.mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: THREE.UniformsUtils.merge([
        THREE.UniformsLib.fog,
        {
          uTime: { value: 0 },
          uShallow: { value: new THREE.Color(opts.shallow ?? (opts.open ? 0x3aa6a0 : 0x2e8c86)) },
          uDeep: { value: new THREE.Color(opts.deep ?? 0x0b2a3a) },
          uSky: { value: new THREE.Color(opts.sky ?? (opts.open ? 0xb4d4ee : 0x9fc2dc)) },
          uSunDir: { value: new THREE.Vector3(-0.45, 0.8, 0.35).normalize() },
          uSunColor: { value: new THREE.Color(1.0, 0.92, 0.8) },
          uOpen: { value: opts.open ? 1 : 0 },
        },
      ]),
      transparent: true,
      depthWrite: false,
      fog: true,
    });
    this.mesh = new THREE.Mesh(geo, this.mat);
    this.mesh.renderOrder = 2;
    this.mesh.name = 'water';
  }

  update(dt: number): void {
    (this.mat.uniforms.uTime as { value: number }).value += dt;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.mat.dispose();
  }
}
